import { useState, type FormEvent } from 'react'
import { useNavigate, Link } from 'react-router-dom'
import { useTranslation } from 'react-i18next'
import { Eye, EyeOff, Monitor } from 'lucide-react'
import { Button } from '@meno/ui/components/ui/button'
import { Input } from '@meno/ui/components/ui/input'
import { Label } from '@meno/ui/components/ui/label'
import { PhoneInput, isValidPhone } from '@meno/ui/components/ui/PhoneInput'
import { LanguageToggle } from '@meno/ui/components/shared/LanguageToggle'
import { useAuth } from '@meno/ui/contexts/AuthContext'

// Special characters = ASCII punctuation ONLY. Arabic (and other non-Latin)
// letters are NOT special characters, so a `[^A-Za-z0-9]` test would wrongly
// accept them — check membership in an explicit ASCII-punctuation set instead.
const SPECIAL_CHARS = "!\"#$%&'()*+,-./:;<=>?@[\\]^_`{|}~"
// Arabic script blocks (base, supplement, extended-A, presentation forms A/B).
const ARABIC_RE = /[؀-ۿݐ-ݿࢠ-ࣿﭐ-﷿ﹰ-﻿]/

function validatePassword(pwd: string): string {
  if (pwd.length < 8) return 'Password must be at least 8 characters long'
  if (!/[A-Z]/.test(pwd)) return 'Password must contain at least one uppercase letter'
  if (!/[a-z]/.test(pwd)) return 'Password must contain at least one lowercase letter'
  if (!/\d/.test(pwd)) return 'Password must contain at least one number'
  if (ARABIC_RE.test(pwd)) return 'Password must not contain Arabic characters'
  if (![...pwd].some(c => SPECIAL_CHARS.includes(c))) return 'Password must contain at least one special character (e.g. !@#$%)'
  return ''
}

function extractErrorMessage(detail: unknown, fallback: string): string {
  if (typeof detail === 'string') return detail
  if (Array.isArray(detail) && detail.length > 0) {
    const msg: string = detail[0]?.msg ?? fallback
    return msg.replace(/^Value error,\s*/i, '')
  }
  return fallback
}

export default function RegisterPage() {
  const { t } = useTranslation()
  const { register } = useAuth()
  const navigate = useNavigate()

  const [name,      setName]      = useState('')
  const [orgName,   setOrgName]   = useState('')
  const [email,     setEmail]     = useState('')
  const [phone,     setPhone]     = useState('')
  const [password,  setPassword]  = useState('')
  const [confirmPwd, setConfirmPwd] = useState('')
  const [showPwd,  setShowPwd]  = useState(false)
  const [showConfirmPwd, setShowConfirmPwd] = useState(false)
  const [loading,    setLoading]    = useState(false)
  const [error,      setError]      = useState('')
  const [pwdError,   setPwdError]   = useState('')
  const [confirmError, setConfirmError] = useState('')
  const [emailTaken, setEmailTaken] = useState(false)

  const handlePasswordChange = (v: string) => {
    setPassword(v)
    setPwdError(v ? validatePassword(v) : '')
    if (confirmPwd) setConfirmError(v === confirmPwd ? '' : t('auth.passwordMismatch', 'Passwords do not match'))
  }

  const handleConfirmChange = (v: string) => {
    setConfirmPwd(v)
    setConfirmError(v && v !== password ? t('auth.passwordMismatch', 'Passwords do not match') : '')
  }

  const handleSubmit = async (e: FormEvent) => {
    e.preventDefault()
    const pwdValidation = validatePassword(password)
    if (pwdValidation) {
      setPwdError(pwdValidation)
      return
    }
    if (password !== confirmPwd) {
      setConfirmError(t('auth.passwordMismatch', 'Passwords do not match'))
      return
    }
    if (!isValidPhone(phone)) {
      setError(t('auth.invalidPhone', 'Please enter a valid phone number.'))
      return
    }
    setError('')
    setEmailTaken(false)
    setLoading(true)
    try {
      await register(name, orgName.trim() || name, email, password, phone || undefined)
      navigate('/overview', { replace: true })
    } catch (err: any) {
      const status = err?.response?.status
      const detail = err?.response?.data?.detail
      if (status === 409 || detail === 'Email already registered') {
        setEmailTaken(true)
      } else {
        setError(extractErrorMessage(detail, t('auth.registerFailed')))
      }
    } finally {
      setLoading(false)
    }
  }

  return (
    <div className="min-h-screen flex">
      {/* ── Left panel ──────────────────────────────────── */}
      <div
        className="hidden lg:flex lg:w-1/2 flex-col justify-between p-12"
        style={{ background: '#0c0f0a' }}
      >
        <div className="flex items-center">
          <img src="/favicon.svg" alt="WeTook" className="h-10 w-auto" />
        </div>

        <div className="text-white space-y-4">
          <h1 className="text-4xl font-bold leading-tight whitespace-pre-wrap">
            {t('auth.registerHeroTitle')}
          </h1>
          <p className="text-white/75 text-lg">
            {t('auth.registerHeroSubtitle')}
          </p>
        </div>

        <div className="flex gap-6 text-white/60 text-sm">
          <span>{t('auth.freeToStart')}</span>
          <span>{t('auth.noCreditCard')}</span>
          <span>{t('auth.instantAccess')}</span>
        </div>
      </div>

      {/* ── Right panel (form) ──────────────────────────── */}
      <div className="flex-1 flex items-center justify-center p-6 bg-background relative">
        <div className="absolute top-4 end-4">
          <LanguageToggle />
        </div>
        <div className="w-full max-w-sm space-y-8">
          <div className="flex lg:hidden items-center justify-center">
            <img src="/favicon.svg" alt="WeTook" className="h-8 w-auto" />
          </div>

          <div className="space-y-1">
            <h2 className="text-2xl font-bold">{t('auth.signUp')}</h2>
            <p className="text-muted-foreground text-sm">{t('auth.createSubtitle')}</p>
          </div>

          <form onSubmit={handleSubmit} className="space-y-4">
            <div className="space-y-1.5">
              <Label htmlFor="name">{t('auth.name')}</Label>
              <Input
                id="name"
                type="text"
                autoComplete="name"
                value={name}
                onChange={e => setName(e.target.value)}
                placeholder="John Smith"
                required
              />
            </div>

            <div className="space-y-1.5">
              <Label htmlFor="orgName">{t('auth.orgName', 'Organization name')}</Label>
              <Input
                id="orgName"
                type="text"
                autoComplete="organization"
                value={orgName}
                onChange={e => setOrgName(e.target.value)}
                placeholder={t('auth.orgNamePlaceholder', 'Acme Coffee Co.')}
                required
              />
            </div>

            <div className="space-y-1.5">
              <Label htmlFor="email">{t('auth.email')}</Label>
              <Input
                id="email"
                type="email"
                autoComplete="email"
                value={email}
                onChange={e => setEmail(e.target.value)}
                placeholder="you@company.com"
                required
              />
            </div>

            <div className="space-y-1.5">
              <Label htmlFor="phone">{t('auth.phone')} <span className="text-muted-foreground">({t('auth.optional')})</span></Label>
              <PhoneInput id="phone" value={phone} onChange={setPhone} />
            </div>

            <div className="space-y-1.5">
              <Label htmlFor="password">{t('auth.password')}</Label>
              <div className="relative">
                <Input
                  id="password"
                  type={showPwd ? 'text' : 'password'}
                  autoComplete="new-password"
                  value={password}
                  onChange={e => handlePasswordChange(e.target.value)}
                  placeholder="••••••••"
                  required
                  className="pe-10"
                />
                <button
                  type="button"
                  className="absolute end-3 top-1/2 -translate-y-1/2 text-muted-foreground hover:text-foreground"
                  onClick={() => setShowPwd(v => !v)}
                  tabIndex={-1}
                >
                  {showPwd ? <EyeOff className="w-4 h-4" /> : <Eye className="w-4 h-4" />}
                </button>
              </div>
              {pwdError && (
                <p className="text-xs text-destructive mt-1">{pwdError}</p>
              )}
            </div>

            <div className="space-y-1.5">
              <Label htmlFor="confirmPassword">{t('auth.confirmPassword', 'Confirm Password')}</Label>
              <div className="relative">
                <Input
                  id="confirmPassword"
                  type={showConfirmPwd ? 'text' : 'password'}
                  autoComplete="new-password"
                  value={confirmPwd}
                  onChange={e => handleConfirmChange(e.target.value)}
                  placeholder="••••••••"
                  required
                  className="pe-10"
                />
                <button
                  type="button"
                  className="absolute end-3 top-1/2 -translate-y-1/2 text-muted-foreground hover:text-foreground"
                  onClick={() => setShowConfirmPwd(v => !v)}
                  tabIndex={-1}
                >
                  {showConfirmPwd ? <EyeOff className="w-4 h-4" /> : <Eye className="w-4 h-4" />}
                </button>
              </div>
              {confirmError && (
                <p className="text-xs text-destructive mt-1">{confirmError}</p>
              )}
            </div>

            {emailTaken && (
              <p className="text-sm text-destructive bg-destructive/10 px-3 py-2 rounded-md">
                {t('auth.emailTaken')}{' '}
                <Link
                  to="/login"
                  state={{ email }}
                  className="underline font-medium"
                >
                  {t('auth.signIn')}
                </Link>
              </p>
            )}

            {error && (
              <p className="text-sm text-destructive bg-destructive/10 px-3 py-2 rounded-md">
                {error}
              </p>
            )}

            <Button type="submit" className="w-full" disabled={loading}>
              {loading ? t('auth.creatingAccount') : t('auth.signUp')}
            </Button>
          </form>

          <p className="text-center text-sm text-muted-foreground">
            {t('auth.haveAccount')}{' '}
            <Link to="/login" className="text-primary font-medium hover:underline">
              {t('auth.signIn')}
            </Link>
          </p>
        </div>
      </div>
    </div>
  )
}
